import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useVehicleImage } from '../hooks/useVehicleImage';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AyvensQuestion } from '../components/ayvens/AyvensQuestion';
import { CanoneMismatchDialog } from '../components/offers/CanoneMismatchDialog';
import { Car, ArrowLeft, Fuel, Settings2, FileDown, User, Mail, Phone, Percent, Gauge, Users, ImageIcon, Plus, BookUser, Eye, EyeOff, CalendarDays } from 'lucide-react';
import { cn } from '../lib/utils';
import { TIPOLOGIE_CLIENTE } from '../lib/tipologieCliente';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente Hero Image per pagina dettaglio veicolo (solo NUOVO)
const VehicleDetailHero = ({ marca, modello, isUsato }) => {
  const { imageUrl } = useVehicleImage(marca, isUsato ? null : modello);
  if (!imageUrl || isUsato) return null;
  // Usa URL HD (w1200) per la pagina dettaglio
  const hdUrl = imageUrl.replace('sz=w400', 'sz=w1200');
  return (
    <div className="w-full bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center px-8 py-6">
      <img 
        src={hdUrl} 
        alt={`${marca} ${modello}`} 
        className="max-w-full max-h-52 object-contain drop-shadow-lg" 
        loading="lazy" 
        onError={(e) => { e.target.parentElement.style.display = 'none'; }} 
      />
      <p className="text-[10px] text-slate-400 italic mt-2 text-center">
        L'immagine è puramente indicativa e può non rappresentare l'allestimento o il colore esatto del veicolo offerto
      </p>
    </div>
  );
};

// Helper per tracciare eventi KPI (calcolo canone)
const trackCalculateQuote = async (vehicleId, getAuthHeader) => {
  try {
    await axios.post(`${API}/track-event`, {
      event_type: 'calculate_quote',
      vehicle_id: vehicleId
    }, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
  } catch (error) {
    // Silently fail - tracking non deve bloccare l'UX
    console.debug('Tracking calculate_quote failed:', error);
  }
};


const DURATIONS = [36, 48, 60];
const KM_OPTIONS = [10000, 15000, 20000, 25000];

// 4Vantage fixed parameters
const VANTAGE_DURATION = 48;
const VANTAGE_KM = 30000;
// LONGRUN: solo veicoli usati, durata 48 mesi, km illimitati
const LONGRUN_DURATION = 48;
const LONGRUN_KM = 'illimitati'; // valore speciale

// ========== FRANCHIGIE TABLES ==========
// Valori in percentuale (es. 2.988 = 2.988% da applicare al canone base)
// Struttura: franchigie[tipo][durata][combinazione][km] = percentuale
const FRANCHIGIE_NUOVO = {
  36: {
    '1000-10': { 10000: 0, 15000: 0, 20000: 0, 25000: 0 }, // Base - nessun extra
    '1000-0': { 10000: 2.988, 15000: 2.788, 20000: 2.644, 25000: 2.497 },
    '500-10': { 10000: 2.934, 15000: 2.737, 20000: 2.596, 25000: 2.356 },
    '500-0': { 10000: 5.848, 15000: 5.496, 20000: 5.202, 25000: 4.800 },
  },
  48: {
    '1000-10': { 10000: 0, 15000: 0, 20000: 0, 25000: 0 },
    '1000-0': { 10000: 3.146, 15000: 2.955, 20000: 2.846, 25000: 2.675 },
    '500-10': { 10000: 3.121, 15000: 2.931, 20000: 2.823, 25000: 2.552 },
    '500-0': { 10000: 6.163, 15000: 5.799, 20000: 5.562, 25000: 5.112 },
  },
  60: {
    '1000-10': { 10000: 0, 15000: 0, 20000: 0, 25000: 0 },
    '1000-0': { 10000: 3.101, 15000: 2.917, 20000: 2.858, 25000: 2.784 },
    '500-10': { 10000: 3.103, 15000: 2.919, 20000: 2.860, 25000: 2.682 },
    '500-0': { 10000: 6.164, 15000: 5.810, 20000: 5.619, 25000: 5.288 },
  },
};

const FRANCHIGIE_USATO = {
  36: {
    '1000-10': { 10000: 0, 15000: 0, 20000: 0, 25000: 0 },
    '1000-0': { 10000: 2.301, 15000: 4.442, 20000: 6.582, 25000: 8.722 },
    '500-10': { 10000: 3.049, 15000: 2.962, 20000: 2.875, 25000: 2.789 },
    '500-0': { 10000: 5.350, 15000: 5.139, 20000: 4.927, 25000: 4.716 },
  },
  48: {
    '1000-10': { 10000: 0, 15000: 0, 20000: 0, 25000: 0 },
    '1000-0': { 10000: 2.490, 15000: 2.302, 20000: 2.115, 25000: 1.927 },
    '500-10': { 10000: 3.324, 15000: 3.146, 20000: 2.967, 25000: 2.789 },
    '500-0': { 10000: 5.814, 15000: 5.448, 20000: 5.082, 25000: 4.716 },
  },
  60: {
    '1000-10': { 10000: 0, 15000: 0, 20000: 0, 25000: 0 },
    '1000-0': { 10000: 2.556, 15000: 2.370, 20000: 2.185, 25000: 1.999 },
    '500-10': { 10000: 3.449, 15000: 3.274, 20000: 3.098, 25000: 2.923 },
    '500-0': { 10000: 6.005, 15000: 5.644, 20000: 5.283, 25000: 4.922 },
  },
};

// ========== GOMME TABLES ==========
// Costo unitario per singola gomma (€/mese)
// Struttura: gomme[tipo][durata] = { invernali: €, estive: € }
const GOMME_COSTI = {
  nuovo: {
    36: { invernali: 6.5675, estive: 3.895 },
    48: { invernali: 5.275, estive: 2.95 },
    60: { invernali: 4.35, estive: 2.375 },
  },
  usato: {
    36: { invernali: 9.8775, estive: 5.6825 },
    48: { invernali: 7.7875, estive: 4.305 },
    60: { invernali: 6.3775, estive: 3.48 },
  },
};

// Opzioni franchigie per il selettore
const FRANCHIGIE_OPTIONS = [
  { value: '1000-10', label: '€1.000 / 10%', descrizione: 'Franchigia Danni €1.000 • Furto/Incendio 10%' },
  { value: '1000-0', label: '€1.000 / 0%', descrizione: 'Franchigia Danni €1.000 • Furto/Incendio 0%' },
  { value: '500-10', label: '€500 / 10%', descrizione: 'Franchigia Danni €500 • Furto/Incendio 10%' },
  { value: '500-0', label: '€500 / 0%', descrizione: 'Franchigia Danni €500 • Furto/Incendio 0%' },
];

// Commission config per tipo veicolo
const COMMISSION_CONFIG = {
  nuovo: { base: 4, maxAggiuntivo: 6 },      // Base 4% + max 6% = 10% (6 scatti)
  usato: { base: 2, maxAggiuntivo: 8 },      // Base 2% + max 8% = 10% (8 scatti)
  '4vantage': { base: 2, maxAggiuntivo: 6 }  // Base 2% + max 6% = 8% (CAP fisso)
};

// Commission multipliers table for NUOVO (base 4%, max +6%)
const PROVVIGIONI_NUOVO = {
  0: { "36_10000": 1.00, "36_15000": 1.00, "36_20000": 1.00, "36_25000": 1.00, "48_10000": 1.00, "48_15000": 1.00, "48_20000": 1.00, "48_25000": 1.00, "60_10000": 1.00, "60_15000": 1.00, "60_20000": 1.00, "60_25000": 1.00 },
  1: { "36_10000": 1.03, "36_15000": 1.03, "36_20000": 1.03, "36_25000": 1.02, "48_10000": 1.02, "48_15000": 1.02, "48_20000": 1.02, "48_25000": 1.02, "60_10000": 1.02, "60_15000": 1.02, "60_20000": 1.02, "60_25000": 1.01 },
  2: { "36_10000": 1.05, "36_15000": 1.05, "36_20000": 1.04, "36_25000": 1.04, "48_10000": 1.04, "48_15000": 1.03, "48_20000": 1.03, "48_25000": 1.03, "60_10000": 1.03, "60_15000": 1.03, "60_20000": 1.03, "60_25000": 1.02 },
  3: { "36_10000": 1.07, "36_15000": 1.07, "36_20000": 1.06, "36_25000": 1.06, "48_10000": 1.05, "48_15000": 1.05, "48_20000": 1.05, "48_25000": 1.05, "60_10000": 1.05, "60_15000": 1.05, "60_20000": 1.04, "60_25000": 1.03 },
  4: { "36_10000": 1.09, "36_15000": 1.08, "36_20000": 1.08, "36_25000": 1.08, "48_10000": 1.07, "48_15000": 1.06, "48_20000": 1.06, "48_25000": 1.06, "60_10000": 1.06, "60_15000": 1.06, "60_20000": 1.05, "60_25000": 1.04 },
  5: { "36_10000": 1.11, "36_15000": 1.10, "36_20000": 1.10, "36_25000": 1.09, "48_10000": 1.08, "48_15000": 1.08, "48_20000": 1.08, "48_25000": 1.08, "60_10000": 1.07, "60_15000": 1.07, "60_20000": 1.07, "60_25000": 1.05 },
  6: { "36_10000": 1.12, "36_15000": 1.12, "36_20000": 1.12, "36_25000": 1.11, "48_10000": 1.10, "48_15000": 1.09, "48_20000": 1.09, "48_25000": 1.09, "60_10000": 1.09, "60_15000": 1.08, "60_20000": 1.08, "60_25000": 1.06 },
};

// Commission multipliers table for USATO (base 2%, max +8%)
const PROVVIGIONI_USATO = {
  0: { "36_10000": 1.00, "36_15000": 1.00, "36_20000": 1.00, "36_25000": 1.00, "48_10000": 1.00, "48_15000": 1.00, "48_20000": 1.00, "48_25000": 1.00, "60_10000": 1.00, "60_15000": 1.00, "60_20000": 1.00, "60_25000": 1.00 },
  1: { "36_10000": 1.03, "36_15000": 1.03, "36_20000": 1.03, "36_25000": 1.02, "48_10000": 1.02, "48_15000": 1.02, "48_20000": 1.02, "48_25000": 1.02, "60_10000": 1.02, "60_15000": 1.02, "60_20000": 1.02, "60_25000": 1.01 },
  2: { "36_10000": 1.05, "36_15000": 1.05, "36_20000": 1.04, "36_25000": 1.04, "48_10000": 1.04, "48_15000": 1.03, "48_20000": 1.03, "48_25000": 1.03, "60_10000": 1.03, "60_15000": 1.03, "60_20000": 1.03, "60_25000": 1.02 },
  3: { "36_10000": 1.07, "36_15000": 1.07, "36_20000": 1.06, "36_25000": 1.06, "48_10000": 1.05, "48_15000": 1.05, "48_20000": 1.05, "48_25000": 1.05, "60_10000": 1.05, "60_15000": 1.05, "60_20000": 1.04, "60_25000": 1.03 },
  4: { "36_10000": 1.09, "36_15000": 1.08, "36_20000": 1.08, "36_25000": 1.08, "48_10000": 1.07, "48_15000": 1.06, "48_20000": 1.06, "48_25000": 1.06, "60_10000": 1.06, "60_15000": 1.06, "60_20000": 1.05, "60_25000": 1.04 },
  5: { "36_10000": 1.11, "36_15000": 1.10, "36_20000": 1.10, "36_25000": 1.09, "48_10000": 1.08, "48_15000": 1.08, "48_20000": 1.08, "48_25000": 1.08, "60_10000": 1.07, "60_15000": 1.07, "60_20000": 1.07, "60_25000": 1.05 },
  6: { "36_10000": 1.12, "36_15000": 1.12, "36_20000": 1.12, "36_25000": 1.11, "48_10000": 1.10, "48_15000": 1.09, "48_20000": 1.09, "48_25000": 1.09, "60_10000": 1.09, "60_15000": 1.08, "60_20000": 1.08, "60_25000": 1.06 },
  7: { "36_10000": 1.14, "36_15000": 1.14, "36_20000": 1.13, "36_25000": 1.13, "48_10000": 1.11, "48_15000": 1.11, "48_20000": 1.10, "48_25000": 1.10, "60_10000": 1.10, "60_15000": 1.10, "60_20000": 1.09, "60_25000": 1.07 },
  8: { "36_10000": 1.16, "36_15000": 1.15, "36_20000": 1.15, "36_25000": 1.14, "48_10000": 1.13, "48_15000": 1.12, "48_20000": 1.12, "48_25000": 1.12, "60_10000": 1.12, "60_15000": 1.11, "60_20000": 1.10, "60_25000": 1.08 },
};

const VehicleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeader, impersonatedUser, getImpersonateParams } = useAuth();
  
  // Leggi parametri passati dal catalogo (se presenti)
  const initialParams = useMemo(() => location.state || {}, [location.state]);
  
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState(initialParams.selectedDuration || null);
  const [selectedKm, setSelectedKm] = useState(initialParams.selectedKm || null);
  const [provScelta, setProvScelta] = useState(0);
  const [use4Vantage, setUse4Vantage] = useState(initialParams.use4Vantage || false);
  // LONGRUN: solo per veicoli usati con canone_longrun > 0
  const [useLongrun, setUseLongrun] = useState(false);
  const [anticipo, setAnticipo] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [canoneMismatch, setCanoneMismatch] = useState(null);
  
  // Franchigie e Gomme state
  const [franchigiaScelta, setFranchigiaScelta] = useState('1000-10'); // Default: base
  const [gommeInvernali, setGommeInvernali] = useState(0); // 0-8
  const [gommeEstive, setGommeEstive] = useState(0); // 0-8
  // Auto sostitutiva
  const [autoSostitutivaEnabled, setAutoSostitutivaEnabled] = useState(false);
  const [autoSostitutivaCategoria, setAutoSostitutivaCategoria] = useState('B>8');
  // Promo Nuovo Cliente / Rinnovo (Nuovo/Usato/Longrun, non 4Vantage)
  const [promoEnabled, setPromoEnabled] = useState(false);
  
  // Stato per nascondere/mostrare provvigioni
  const [showProvvigioni, setShowProvvigioni] = useState(false);
  
  // Tracking KPI: flag per evitare tracking multipli nella stessa sessione
  const [hasTrackedQuoteCalc, setHasTrackedQuoteCalc] = useState(false);
  
  // Contacts from rubrica
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [selectedContactOwnerCompetenza, setSelectedContactOwnerCompetenza] = useState(null); // Competenza agente owner
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactTipo, setNewContactTipo] = useState('smart');
  const [newContactAyvens, setNewContactAyvens] = useState(null);
  const [newContactData, setNewContactData] = useState({
    nome_cognome: '',
    telefono: '',
    email: '',
    citta: '',
    responsabile: '',
    tipologia_cliente: '',
    codice_fiscale: '',
    partita_iva: '',
    indirizzo: '',
    cap: '',
    km_annui_previsti: ''
  });
  const [notes, setNotes] = useState('');
  const [dbParams, setDbParams] = useState(null);

  // Auto-disabilita Promo quando 4Vantage è attivo (non applicabile su listini 4Vantage)
  useEffect(() => {
    if (use4Vantage && promoEnabled) setPromoEnabled(false);
  }, [use4Vantage, promoEnabled]);

  // Carica parametri di calcolo dal DB (se disponibili)
  useEffect(() => {
    const loadParams = async () => {
      try {
        const res = await axios.get(`${API}/public/parametri-calcolo/gomme-franchigie`, { headers: getAuthHeader() });
        if (res.data && (res.data.gomme_costi || res.data.franchigie || res.data.provvigioni)) {
          setDbParams(res.data);
        }
      } catch { /* usa default hardcoded */ }
    };
    loadParams();
  }, [getAuthHeader]);

  const fetchContacts = useCallback(async () => {
    try {
      // Passa view_as per segregazione durante impersonificazione
      const impersonateParams = getImpersonateParams();
      const queryParams = impersonateParams.view_as ? `?view_as=${impersonateParams.view_as}` : '';
      const response = await axios.get(`${API}/contacts${queryParams}`, { headers: getAuthHeader() });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [getAuthHeader, getImpersonateParams]);

  const fetchVehicle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles/${id}`, { headers: getAuthHeader() });
      setVehicle(response.data);
      
      // Se i parametri sono già impostati dal catalogo, non sovrascrivere
      if (selectedDuration && selectedKm) {
        return;
      }
      
      // Altrimenti auto-select first available quote
      const quotations = response.data.quotations || {};
      for (const dur of DURATIONS) {
        for (const km of KM_OPTIONS) {
          const key = `${dur}_${km}`;
          if (quotations[key] && quotations[key] > 0) {
            setSelectedDuration(dur);
            setSelectedKm(km);
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast.error('Veicolo non trovato');
      navigate('/dashboard/catalogo');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeader, navigate, selectedDuration, selectedKm]);

  useEffect(() => {
    fetchVehicle();
    fetchContacts();
  }, [fetchVehicle, fetchContacts]);

  // Recupera competenza dell'agente owner quando viene selezionato un contatto
  useEffect(() => {
    const fetchOwnerCompetenza = async () => {
      if (!selectedContactId || user?.role !== 'admin') {
        setSelectedContactOwnerCompetenza(null);
        return;
      }
      
      // Trova il contatto selezionato
      const contact = contacts.find(c => c.id === selectedContactId);
      if (!contact?.owner_id || contact.owner_id === user?.id) {
        setSelectedContactOwnerCompetenza(null);
        return;
      }
      
      // Il contatto ha un owner (agente), recupera la sua competenza
      try {
        const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
        const agent = response.data.find(u => u.id === contact.owner_id);
        if (agent) {
          setSelectedContactOwnerCompetenza(agent.competenza_provvigioni || 100);
        } else {
          setSelectedContactOwnerCompetenza(null);
        }
      } catch (error) {
        console.error('Error fetching agent competenza:', error);
        setSelectedContactOwnerCompetenza(null);
      }
    };
    
    fetchOwnerCompetenza();
  }, [selectedContactId, contacts, user, getAuthHeader]);

  // ============== TRACKING KPI: Calcolo Canone ==============
  // Traccia quando l'utente modifica parametri per la prima volta in questa sessione
  useEffect(() => {
    // Traccia solo se:
    // - Veicolo caricato
    // - L'utente ha modificato almeno un parametro rispetto ai default
    // - Non abbiamo già tracciato in questa sessione
    if (!vehicle || hasTrackedQuoteCalc) return;
    
    // Verifica se l'utente ha personalizzato qualcosa (non i valori di default)
    const hasCustomized = (
      (selectedDuration !== null && selectedDuration !== initialParams.selectedDuration) ||
      (selectedKm !== null && selectedKm !== initialParams.selectedKm) ||
      franchigiaScelta !== '1000-10' ||
      gommeInvernali > 0 ||
      gommeEstive > 0 ||
      anticipo > 0 ||
      provScelta > 0
    );
    
    if (hasCustomized) {
      trackCalculateQuote(vehicle.id, getAuthHeader);
      setHasTrackedQuoteCalc(true);
    }
  }, [vehicle, selectedDuration, selectedKm, franchigiaScelta, gommeInvernali, gommeEstive, anticipo, provScelta, hasTrackedQuoteCalc, initialParams, getAuthHeader]);

  // Determine vehicle type first
  const isUsato = vehicle?.tipo === 'usato';
  const has4Vantage = vehicle?.canone_4vantage && vehicle?.canone_4vantage > 0;
  // LONGRUN: disponibile solo per veicoli usato con canone_longrun > 0
  const hasLongrun = isUsato && vehicle?.canone_longrun && vehicle?.canone_longrun > 0;
  // Mutua esclusione: se attivo Longrun, 4Vantage non si applica (e viceversa)
  // (per gli usati non esiste 4Vantage comunque, ma per sicurezza)
  const activeLongrun = useLongrun && hasLongrun;

  const getQuote = (duration, km) => {
    const key = `${duration}_${km}`;
    return vehicle?.quotations?.[key] || 0;
  };

  // Get commission config based on vehicle type AND 4Vantage mode
  // 4Vantage ha sempre base 2% con cap 8% (max +6%)
  const getCommissionConfig = () => {
    // Usa parametri dal DB se disponibili
    const dbConfig = dbParams?.commission_config;
    if (dbConfig) {
      if (use4Vantage && has4Vantage) return dbConfig['4vantage'] || { base: 2, maxAggiuntivo: 6 };
      return isUsato ? (dbConfig.usato || COMMISSION_CONFIG.usato) : (dbConfig.nuovo || COMMISSION_CONFIG.nuovo);
    }
    if (use4Vantage && has4Vantage) return COMMISSION_CONFIG['4vantage'];
    return isUsato ? COMMISSION_CONFIG.usato : COMMISSION_CONFIG.nuovo;
  };
  
  const commissionConfig = getCommissionConfig();
  
  // Provvigioni: usa DB se disponibile
  const getProvvigioni = () => {
    const dbProv = dbParams?.provvigioni;
    const tipoKey = (use4Vantage && has4Vantage) ? '4vantage' : (isUsato ? 'usato' : 'nuovo');
    if (dbProv && dbProv[tipoKey]?.data) {
      // Converti dal formato DB [{prov_scelta, moltiplicatori}] a {prov_scelta: {key: value}}
      const table = {};
      for (const step of dbProv[tipoKey].data) {
        table[step.prov_scelta] = step.moltiplicatori;
      }
      return table;
    }
    // 4Vantage senza tabella dedicata: usa la tabella NUOVO del DB (come il server)
    if (tipoKey === '4vantage' && dbProv?.nuovo?.data) {
      const table = {};
      for (const step of dbProv.nuovo.data) table[step.prov_scelta] = step.moltiplicatori;
      return table;
    }
    return isUsato ? PROVVIGIONI_USATO : PROVVIGIONI_NUOVO;
  };
  const provvigioni = getProvvigioni();
  const provBase = commissionConfig.base;
  const provMaxAggiuntivo = commissionConfig.maxAggiuntivo;
  const provReale = provBase + provScelta;

  // Reset provScelta quando cambia la modalità 4Vantage (per rispettare il cap)
  useEffect(() => {
    const config = use4Vantage && has4Vantage 
      ? COMMISSION_CONFIG['4vantage'] 
      : (isUsato ? COMMISSION_CONFIG.usato : COMMISSION_CONFIG.nuovo);
    
    // Se provScelta supera il nuovo max, resetta al max consentito
    if (provScelta > config.maxAggiuntivo) {
      setProvScelta(config.maxAggiuntivo);
    }
  }, [use4Vantage, has4Vantage, isUsato, provScelta]);

  // Get multiplier for a specific duration/km combination
  const getMultiplier = (duration, km) => {
    const key = `${duration}_${km}`;
    return provvigioni[provScelta]?.[key] || 1;
  };

  // Get quote with commission applied
  const getQuoteWithCommission = (duration, km) => {
    const baseQuote = getQuote(duration, km);
    if (baseQuote <= 0) return 0;
    const multiplier = getMultiplier(duration, km);
    return baseQuote * multiplier;
  };

  const selectedQuote = selectedDuration && selectedKm ? getQuote(selectedDuration, selectedKm) : 0;
  const canEditCommission = ['admin', 'super_admin', 'top_agent', 'agente'].includes(user?.role);

  // Check if user is client/prospect (not admin/agente)
  const isClientOrProspect = !canEditCommission;
  
  // Ricarico percentuale per clienti/prospect
  // Auto Nuove: +2%, Auto Usate: +4%, 4Vantage: +2%
  const ricaricoPct = isClientOrProspect ? (isUsato ? 4 : 2) : 0;
  
  // Apply markup for clients (round up to nearest euro)
  const applyClientMarkup = (price) => {
    if (!isClientOrProspect || price <= 0) return price;
    return Math.ceil(price * (1 + ricaricoPct / 100));
  };
  
  // Get quote with client markup applied (no commission multiplier for clients)
  const getQuoteForDisplay = (duration, km) => {
    const baseQuote = getQuote(duration, km);
    if (baseQuote <= 0) return 0;
    if (isClientOrProspect) {
      // Clienti: solo ricarico, no moltiplicatore provvigione
      return applyClientMarkup(baseQuote);
    } else {
      // Agenti/Admin: moltiplicatore provvigione
      const multiplier = getMultiplier(duration, km);
      return baseQuote * multiplier;
    }
  };
  
  // Quando 4Vantage o Longrun sono attivi, usa durata/km fissi
  // LONGRUN: 48 mesi km illimitati (per moltiplicatore provvigioni uso schema usato 48/15000)
  const effectiveDuration = activeLongrun
    ? LONGRUN_DURATION
    : (use4Vantage && has4Vantage ? VANTAGE_DURATION : selectedDuration);
  const effectiveKm = activeLongrun
    ? 15000  // schema provvigionale usato 48 mesi - 15.000 km
    : (use4Vantage && has4Vantage ? VANTAGE_KM : selectedKm);
  
  // Calculate multiplier and final price
  // For 4Vantage and Longrun, use schema 48_<km> (usato)
  const quoteKey = effectiveDuration && effectiveKm ? `${effectiveDuration}_${Math.min(effectiveKm, 25000)}` : null;
  const moltiplicatore = isClientOrProspect ? 1.0 : (quoteKey ? (provvigioni[provScelta]?.[quoteKey] || 1) : 1);
  
  // Get base price: longrun → canone_longrun, 4vantage → canone_4vantage, altrimenti standard
  const canoneBase = activeLongrun
    ? applyClientMarkup(vehicle?.canone_longrun)
    : (use4Vantage && has4Vantage 
        ? applyClientMarkup(vehicle?.canone_4vantage)
        : applyClientMarkup(selectedQuote));
  
  // ========== CALCOLO FRANCHIGIE ==========
  // Percentuale extra da applicare al canone base
  const getFranchigiaPercentuale = () => {
    if (use4Vantage && has4Vantage) return 0;
    if (!effectiveDuration || !effectiveKm) return 0;
    
    // Usa matrici dal DB se disponibili
    const tipo = isUsato ? 'usato' : 'nuovo';
    const dbMatrici = dbParams?.franchigie?.matrici;
    if (dbMatrici && dbMatrici[tipo]) {
      const kmKey = String(Math.min(effectiveKm, 25000));
      return dbMatrici[tipo]?.[String(effectiveDuration)]?.[franchigiaScelta]?.[kmKey] || 0;
    }
    
    const tabella = isUsato ? FRANCHIGIE_USATO : FRANCHIGIE_NUOVO;
    const kmKey = Math.min(effectiveKm, 25000);
    return tabella[effectiveDuration]?.[franchigiaScelta]?.[kmKey] || 0;
  };
  
  const franchigiaPercentuale = getFranchigiaPercentuale();
  const extraFranchigia = canoneBase * (franchigiaPercentuale / 100);
  
  // ========== CALCOLO GOMME ==========
  const getGommeCosto = () => {
    if (use4Vantage && has4Vantage) return 0;
    if (!effectiveDuration) return 0;
    
    const tipo = isUsato ? 'usato' : 'nuovo';
    
    // Usa costi dal DB se disponibili
    const dbGomme = dbParams?.gomme_costi;
    if (dbGomme && dbGomme[tipo]?.[String(effectiveDuration)]) {
      const costi = dbGomme[tipo][String(effectiveDuration)];
      return (gommeInvernali * (costi.invernali || 0)) + (gommeEstive * (costi.estive || 0));
    }
    
    const costi = GOMME_COSTI[tipo]?.[effectiveDuration];
    if (!costi) return 0;
    
    const costoInvernali = gommeInvernali * costi.invernali;
    const costoEstive = gommeEstive * costi.estive;
    return costoInvernali + costoEstive;
  };
  
  const costoGomme = getGommeCosto();
  
  // Helper per il display del prezzo unitario/pz (usa DB con fallback hardcoded)
  const getPrezzoGommaUnitario = (kind) => {
    if (!effectiveDuration) return 0;
    const tipo = isUsato ? 'usato' : 'nuovo';
    const dbGomme = dbParams?.gomme_costi;
    if (dbGomme && dbGomme[tipo]?.[String(effectiveDuration)]) {
      return dbGomme[tipo][String(effectiveDuration)][kind] || 0;
    }
    return GOMME_COSTI[tipo]?.[effectiveDuration]?.[kind] || 0;
  };
  
  // ========== CALCOLO AUTO SOSTITUTIVA ==========
  // Costo mensile fisso (indipendente da durata/km) caricato dai parametri DB
  const getCostoAutoSostitutiva = () => {
    if (!autoSostitutivaEnabled || !autoSostitutivaCategoria) return 0;
    const tipo = use4Vantage ? '4vantage' : (isUsato ? 'usato' : 'nuovo');
    const tabella = dbParams?.auto_sostitutiva_costi;
    if (!tabella || !tabella[autoSostitutivaCategoria]) return 0;
    return Number(tabella[autoSostitutivaCategoria][tipo]) || 0;
  };
  const costoAutoSostitutiva = getCostoAutoSostitutiva();

  // ========== CALCOLO PROMO NUOVO CLIENTE / RINNOVO ==========
  // Sorgente importi: parametri_calcolo (default 800 Nuovo / 1000 Usato/Longrun)
  // Longrun è sempre USATO. 4Vantage: promo NON applicabile → auto-disable
  const promoImporto = (() => {
    if (use4Vantage) return 0;
    const tipoUsato = isUsato; // longrun implica isUsato=true
    const val = tipoUsato ? dbParams?.promo_usato : dbParams?.promo_nuovo;
    return Number(val ?? (tipoUsato ? 1000 : 800));
  })();
  const promoAttiva = promoEnabled && !use4Vantage;
  const scontoPromo = (promoAttiva && effectiveDuration > 0) ? (promoImporto / effectiveDuration) : 0;

  // Calculate discount from anticipo (anticipo / durata mesi)
  const scontoAnticipo = effectiveDuration ? (anticipo / effectiveDuration) : 0;
  
  // ========== FORMULA FINALE ==========
  // Canone Finale = (Canone Base × Moltiplicatore Provvigionale) + Franchigia + Gomme + Auto Sostitutiva - Sconto Anticipo - Sconto Promo
  const canoneConProvvigione = Math.round(canoneBase * moltiplicatore);
  const finalPrice = Math.max(0, canoneConProvvigione + extraFranchigia + costoGomme + costoAutoSostitutiva - scontoAnticipo - scontoPromo);
  
  // Calculate provvigione totale and agente (only for agents/admin)
  const prezzoListino = vehicle?.prezzo || 0;
  const provvigioneTotale = canEditCommission ? (prezzoListino * provReale / 100) : 0;
  
  // Competenza: PRIORITÀ
  // 1) Se impersonando un agente, usa la sua competenza
  // 2) Se selezionato un contatto con owner agente, usa la competenza di quell'agente
  // 3) Altrimenti usa la propria competenza
  const competenzaAgente = impersonatedUser 
    ? (impersonatedUser.competenza_provvigioni || 30)
    : selectedContactOwnerCompetenza !== null 
      ? selectedContactOwnerCompetenza 
      : (user?.competenza_provvigioni || 100);
  
  // FORMULA GERARCHICA PROVVIGIONI:
  // 1) Sede Centrale detiene il 100% del valore
  // 2) Reparto riceve commission_split% della Sede (es. 70%)
  // 3) TOP_AGENT e ADMIN prendono l'intera quota del Reparto (NO doppia moltiplicazione)
  // 4) AGENTE riceve competenza% di quanto assegnato al Reparto
  const repartoCommissionSplit = impersonatedUser?.reparto_commission_split || user?.reparto_commission_split || 100;
  const provvigioneReparto = provvigioneTotale * repartoCommissionSplit / 100;
  
  // Determina il ruolo operativo (impersonato o reale)
  const ruoloOperativo = impersonatedUser?.role || user?.role;
  
  // TOP_AGENT e ADMIN prendono l'intera quota reparto, AGENTE applica la sua competenza
  const isRuoloApicale = ['top_agent', 'admin'].includes(ruoloOperativo);
  const provvigioneAgente = canEditCommission 
    ? (isRuoloApicale ? provvigioneReparto : (provvigioneReparto * competenzaAgente / 100))
    : 0;

  const handleCreateNewContact = async () => {
    if (newContactAyvens === null) {
      toast.error('Indica se il cliente è già cliente Ayvens / ALD Automotive');
      throw new Error('ayvens-required');
    }
    const payload = {
      gia_cliente_ayvens: newContactAyvens,
      tipo_profilo: newContactTipo,
      nome_cognome: newContactData.nome_cognome,
      telefono: newContactData.telefono,
      email: newContactData.email,
      citta: newContactData.citta || null,
      responsabile: newContactData.responsabile || null,
      tipologia_cliente: newContactData.tipologia_cliente || null,
      codice_fiscale: newContactData.codice_fiscale || null,
      partita_iva: newContactData.partita_iva || null,
      indirizzo: newContactData.indirizzo || null,
      cap: newContactData.cap || null,
      km_annui_previsti: newContactData.km_annui_previsti ? parseInt(newContactData.km_annui_previsti) : null
    };
    
    try {
      const response = await axios.post(`${API}/contacts`, payload, { headers: getAuthHeader() });
      toast.success('Contatto creato!');
      await fetchContacts();
      setSelectedContactId(response.data.id);
      setShowNewContactForm(false);
      setNewContactData({
        nome_cognome: '', telefono: '', email: '', citta: '', responsabile: '',
        tipologia_cliente: '', codice_fiscale: '', partita_iva: '', indirizzo: '', cap: '', km_annui_previsti: ''
      });
      return response.data.id;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione contatto');
      throw error;
    }
  };

  const handleCreateOffer = async () => {
    // Per Longrun: durata fissa 48 mesi, km illimitati (salvato come 999999 nel DB per identificazione)
    // Per 4Vantage: fixed values 48/30000
    let offerDuration, offerKm;
    if (activeLongrun) {
      offerDuration = LONGRUN_DURATION;
      offerKm = 999999;  // marker per "illimitato"
    } else if (use4Vantage && has4Vantage) {
      offerDuration = VANTAGE_DURATION;
      offerKm = VANTAGE_KM;
    } else {
      offerDuration = selectedDuration;
      offerKm = selectedKm;
    }
    
    if (!offerDuration || !offerKm || canoneBase === 0) {
      toast.error('Seleziona una combinazione valida');
      return;
    }

    // Per clienti: niente selezione contatto, il backend associa automaticamente
    let contactId = null;
    
    if (!isClientOrProspect) {
      // Check contact selection solo per Admin/Agente
      contactId = selectedContactId;
      if (showNewContactForm) {
        // Create new contact first
        try {
          contactId = await handleCreateNewContact();
        } catch {
          return; // Error already handled
        }
      }
      
      if (!contactId) {
        toast.error('Seleziona o crea un contatto dalla rubrica');
        return;
      }
    }

    await submitOffer({
        vehicle_id: id,
        durata_mesi: offerDuration,
        km_annui: offerKm,
        anticipo: anticipo,
        prov_scelta: canEditCommission ? provScelta : undefined,
        use_4vantage: use4Vantage && !activeLongrun,
        use_longrun: activeLongrun,
        contact_id: contactId,  // null per clienti (backend auto-associa)
        notes: notes || null,
        // Nuovi parametri franchigie e gomme - tutti modificabili con Longrun
        franchigia_scelta: use4Vantage && !activeLongrun ? '500-0' : franchigiaScelta,
        gomme_invernali: use4Vantage && !activeLongrun ? 4 : gommeInvernali,
        gomme_estive: use4Vantage && !activeLongrun ? 0 : gommeEstive,
        extra_franchigia: extraFranchigia,
        costo_gomme: costoGomme,
        // Auto sostitutiva (opzionale)
        auto_sostitutiva_categoria: autoSostitutivaEnabled ? autoSostitutivaCategoria : null,
        costo_auto_sostitutiva: autoSostitutivaEnabled ? costoAutoSostitutiva : 0,
        // Promo Nuovo Cliente / Rinnovo (attivabile solo su Nuovo/Usato/Longrun, non 4Vantage)
        promo_applicata: promoAttiva,
        // Impersonificazione: passa l'ID dell'agente impersonato se attivo
        impersonate_user_id: impersonatedUser?.id || null,
        canone_atteso: Math.round(finalPrice),
    });
  };

  const submitOffer = async (payload) => {
    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/offers`, payload, { headers: getAuthHeader() });

      toast.success('Preventivo creato con successo!');
      setIsDialogOpen(false);
      
      // Download PDF
      const pdfResponse = await axios.get(`${API}/offers/${response.data.id}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preventivo_${response.data.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === 'CANONE_MISMATCH') {
        setCanoneMismatch({ payload, atteso: detail.canone_atteso, server: detail.canone_server });
      } else {
        toast.error((typeof detail === 'string' && detail) || detail?.message || 'Errore nella creazione del preventivo', detail?.code === 'VEHICLE_NOT_AVAILABLE' ? { duration: 10000 } : undefined);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const confirmCanoneMismatch = () => {
    const { payload } = canoneMismatch;
    setCanoneMismatch(null);
    submitOffer({ ...payload, conferma_differenza: true });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 bg-slate-200 rounded-lg animate-pulse" />
          <div className="h-96 bg-slate-200 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (!vehicle) return null;

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/dashboard/catalogo')}
        className="gap-2"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Torna al catalogo
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vehicle Info & Quotation Matrix */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vehicle Header */}
          <Card className={isUsato ? 'border-emerald-200' : ''}>
            <CardContent className="p-0">
              {/* Hero Image */}
              <VehicleDetailHero marca={vehicle.marca} modello={vehicle.modello} isUsato={isUsato} />
              
              <div className="p-6 pt-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium ${isUsato ? 'text-emerald-600' : 'text-primary'}`}>{vehicle.marca}</p>
                    <Badge variant="outline" className={isUsato ? 'border-emerald-500 text-emerald-700' : ''}>
                      {isUsato ? 'USATO' : 'NUOVO'}
                    </Badge>
                    {has4Vantage && (
                      <Badge className="bg-violet-600">4VANTAGE</Badge>
                    )}
                  </div>
                  <h1 className="text-2xl font-bold text-slate-800 mt-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {vehicle.modello}
                  </h1>
                  <p className="text-slate-600 mt-2">{vehicle.versione}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    {/* Vehicle ID - identificativo univoco */}
                    {vehicle.vehicle_id && (
                      <Badge variant="outline" className="font-mono text-xs bg-slate-100 border-slate-300">
                        ID: {vehicle.vehicle_id}
                      </Badge>
                    )}
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Fuel className="w-3 h-3" />
                      {vehicle.fuel}
                    </Badge>
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Settings2 className="w-3 h-3" />
                      {vehicle.cambio}
                    </Badge>
                    <Badge variant="outline">Segmento {vehicle.segmento}</Badge>
                    {vehicle.colore && <Badge variant="outline">{vehicle.colore}</Badge>}
                    {vehicle.targa && <Badge variant="outline" className="mono">{vehicle.targa}</Badge>}
                    {isUsato && vehicle.km && (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                        <Gauge className="w-3 h-3 mr-1" />
                        {vehicle.km.toLocaleString('it-IT')} km
                      </Badge>
                    )}
                    {/* Disponibilità (Nuovo) o Data Immatricolazione (Usato) */}
                    {isUsato ? (
                      vehicle.immatricolazione && (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                          <CalendarDays className="w-3 h-3 mr-1" />
                          Immatricolazione: {vehicle.immatricolazione}
                        </Badge>
                      )
                    ) : (
                      vehicle.disponibilita && (
                        <Badge variant="outline" className="border-primary/50 text-primary">
                          <CalendarDays className="w-3 h-3 mr-1" />
                          Disponibile: {vehicle.disponibilita}
                        </Badge>
                      )
                    )}
                  </div>
                  
                  {/* Badge informativo: questo veicolo supporta anche 4VANTAGE */}
                  {has4Vantage && !use4Vantage && (
                    <div 
                      className="mt-4 p-3 bg-gradient-to-r from-violet-50 to-violet-100 rounded-lg border border-violet-200 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setUse4Vantage(true)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-violet-700">
                            💡 Disponibile anche in <span className="font-bold">4VANTAGE</span>
                          </p>
                          <p className="text-xs text-violet-600 mt-1">
                            Canone fisso €{vehicle.canone_4vantage?.toLocaleString('it-IT')}/mese (48 mesi • 30.000 km)
                          </p>
                        </div>
                        <Badge className="bg-violet-600">
                          Attiva →
                        </Badge>
                      </div>
                    </div>
                  )}
                  
                  {vehicle.link_immagine && (
                    <a href={vehicle.link_immagine} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm text-emerald-600 hover:underline">
                      <ImageIcon className="w-4 h-4" />
                      Vedi tutte le foto (360°)
                    </a>
                  )}
                </div>
              </div>
              
              {/* Image Gallery for Used Vehicles */}
              {isUsato && vehicle.link_immagine && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Foto del veicolo
                    </p>
                    <a 
                      href={vehicle.link_immagine} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Vedi foto 360°
                    </a>
                  </div>
                  
                  {/* Try to embed, with fallback message */}
                  <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden">
                    <iframe
                      src={vehicle.link_immagine}
                      className="absolute inset-0 w-full h-full border-0"
                      title="Foto veicolo 360°"
                      allow="autoplay; fullscreen"
                      allowFullScreen
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 hover:opacity-100 transition-opacity bg-black/50">
                      <p className="text-white text-sm">Clicca per interagire con la vista 360°</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quotation Matrix */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {activeLongrun ? 'Listino LONGRUN' : (use4Vantage && has4Vantage ? 'Listino 4VANTAGE' : 'Matrice Quotazioni')}
              </CardTitle>
              <p className="text-sm text-slate-500">
                {activeLongrun
                  ? 'Condizioni fisse: 48 mesi • Km ILLIMITATI'
                  : (use4Vantage && has4Vantage 
                      ? 'Condizioni fisse: 48 mesi • 30.000 km/anno' 
                      : 'Seleziona durata e chilometraggio per il tuo preventivo')}
              </p>
            </CardHeader>
            <CardContent>
              {activeLongrun ? (
                /* Longrun: Single fixed option - tema MARRONE */
                <div className="rounded-xl p-6 text-center border-2" style={{ backgroundColor: '#fdf5ef', borderColor: '#8B4513' }}>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Gauge className="w-6 h-6" style={{ color: '#8B4513' }} />
                    <span className="font-semibold text-lg" style={{ color: '#6b3410' }}>Listino LONGRUN - Veicoli Usati</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#c89a6f' }}>
                      <p className="text-sm font-medium" style={{ color: '#6b3410' }}>Durata</p>
                      <p className="text-2xl font-bold" style={{ color: '#6b3410' }}>48 mesi</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#c89a6f' }}>
                      <p className="text-sm font-medium" style={{ color: '#6b3410' }}>Km Annui</p>
                      <p className="text-2xl font-bold" style={{ color: '#6b3410' }}>ILLIMITATI</p>
                    </div>
                  </div>
                  <div className="rounded-lg p-4" style={{ backgroundColor: '#8B4513' }}>
                    <p className="text-sm" style={{ color: '#fdf5ef' }}>Canone Finale</p>
                    <p className="text-3xl font-bold text-white">
                      €{Math.round(finalPrice).toLocaleString('it-IT')}<span className="text-lg font-normal">/mese</span>
                    </p>
                  </div>
                  <p className="text-xs text-red-600 mt-3 font-medium">* IVA esclusa</p>
                </div>
              ) : use4Vantage && has4Vantage ? (
                /* 4Vantage: Single fixed option */
                <div className="bg-violet-50 border-2 border-violet-300 rounded-xl p-6 text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Users className="w-6 h-6 text-violet-600" />
                    <span className="text-violet-800 font-semibold text-lg">Listino Dipendenti & P.IVA Forfettarie</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4 border border-violet-200">
                      <p className="text-sm text-violet-600 font-medium">Durata</p>
                      <p className="text-2xl font-bold text-violet-800">48 mesi</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-violet-200">
                      <p className="text-sm text-violet-600 font-medium">Km Annui</p>
                      <p className="text-2xl font-bold text-violet-800">30.000</p>
                    </div>
                  </div>
                  <div className="bg-violet-600 rounded-lg p-4">
                    <p className="text-violet-100 text-sm">Canone Finale</p>
                    <p className="text-3xl font-bold text-white">
                      €{Math.round(finalPrice).toLocaleString('it-IT')}<span className="text-lg font-normal">/mese</span>
                    </p>
                  </div>
                  <p className="text-xs text-red-600 mt-3 font-medium">* IVA esclusa</p>
                </div>
              ) : (
                /* Standard matrix */
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="quotation-matrix">
                      <thead>
                        <tr>
                          <th className="table-header p-3 text-left rounded-tl-lg">Mesi \ KM</th>
                          {KM_OPTIONS.map(km => (
                            <th key={km} className="table-header p-3 text-center last:rounded-tr-lg">
                              {km.toLocaleString('it-IT')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DURATIONS.map(duration => (
                          <tr key={duration}>
                            <td className="p-3 font-semibold text-slate-700 bg-slate-50 border border-slate-200">
                              {duration} mesi
                            </td>
                            {KM_OPTIONS.map(km => {
                              const baseQuote = getQuote(duration, km);
                              const quoteForDisplay = getQuoteForDisplay(duration, km);
                              const isSelected = selectedDuration === duration && selectedKm === km;
                              const isAvailable = baseQuote > 0;
                              
                              return (
                                <td 
                                  key={km}
                                  className={cn(
                                    'quote-cell',
                                    isSelected && (isUsato ? 'bg-emerald-600 text-white' : 'selected'),
                                    !isAvailable && 'bg-slate-50 text-slate-300 cursor-not-allowed'
                                  )}
                                  onClick={() => isAvailable && (setSelectedDuration(duration), setSelectedKm(km))}
                                  data-testid={`quote-${duration}-${km}`}
                                >
                                  {isAvailable ? (
                                    <span className="font-semibold">€{Math.round(quoteForDisplay).toLocaleString('it-IT')}</span>
                                  ) : (
                                    <span>N/D</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-red-600 mt-3 text-center font-medium">* Tutti i canoni sono IVA esclusa</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Configuration Panel */}
        <div className="space-y-6">
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Configura Offerta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Selected quote summary */}
              {activeLongrun ? (
                /* Longrun configuration summary - tema MARRONE */
                <div className="p-4 rounded-lg border" style={{ backgroundColor: '#fdf5ef', borderColor: '#8B4513' }}>
                  <p className="text-sm" style={{ color: '#6b3410' }}>Listino LONGRUN selezionato</p>
                  <p className="font-semibold mt-1" style={{ color: '#6b3410' }}>
                    {LONGRUN_DURATION} mesi • Km ILLIMITATI
                  </p>
                  <p className="text-2xl font-bold mt-2" style={{ color: '#8B4513' }}>
                    €{Math.round(finalPrice).toLocaleString('it-IT')}<span className="text-sm font-normal">/mese</span>
                  </p>
                </div>
              ) : use4Vantage && has4Vantage ? (
                /* 4Vantage configuration summary */
                <div className="p-4 rounded-lg border bg-violet-50 border-violet-200">
                  <p className="text-sm text-violet-600">Listino 4VANTAGE selezionato</p>
                  <p className="font-semibold text-violet-800 mt-1">
                    {VANTAGE_DURATION} mesi • {VANTAGE_KM.toLocaleString('it-IT')} km/anno
                  </p>
                  <p className="text-2xl font-bold mt-2 text-violet-700">
                    €{Math.round(finalPrice).toLocaleString('it-IT')}<span className="text-sm font-normal">/mese</span>
                  </p>
                </div>
              ) : selectedDuration && selectedKm && selectedQuote > 0 ? (
                <div className={`p-4 rounded-lg border ${isUsato ? 'bg-emerald-50 border-emerald-200' : 'bg-primary/5 border-primary/20'}`}>
                  <p className="text-sm text-slate-600">Configurazione selezionata</p>
                  <p className="font-semibold text-slate-800 mt-1">
                    {selectedDuration} mesi • {selectedKm.toLocaleString('it-IT')} km/anno
                  </p>
                  <p className={`text-2xl font-bold mt-2 ${isUsato ? 'text-emerald-600' : 'text-primary'}`}>
                    €{Math.round(finalPrice).toLocaleString('it-IT')}<span className="text-sm font-normal">/mese</span>
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                  <p className="text-slate-500">Seleziona una quotazione dalla matrice</p>
                </div>
              )}

              {/* 4Vantage option */}
              {has4Vantage && (
                <div className={`p-4 rounded-lg border ${use4Vantage ? 'bg-violet-100 border-violet-400' : 'bg-violet-50 border-violet-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-violet-600" />
                      <Label className="text-violet-800 font-medium">Listino 4VANTAGE</Label>
                    </div>
                    <Switch
                      checked={use4Vantage}
                      onCheckedChange={(v) => { setUse4Vantage(v); if (v) setUseLongrun(false); }}
                      data-testid="4vantage-switch"
                    />
                  </div>
                  <p className="text-xs text-violet-600 mt-2">
                    Per dipendenti e P.IVA forfettarie
                  </p>
                  <p className="text-xs text-violet-700 mt-1 font-medium">
                    Condizioni fisse: 48 mesi • 30.000 km/anno
                  </p>
                </div>
              )}

              {/* LONGRUN option (solo Usato) */}
              {hasLongrun && (
                <div
                  className="p-4 rounded-lg border"
                  style={{
                    backgroundColor: activeLongrun ? '#f3e6d3' : '#fdf5ef',
                    borderColor: activeLongrun ? '#8B4513' : '#c89a6f'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Gauge className="w-4 h-4" style={{ color: '#8B4513' }} />
                      <Label className="font-medium" style={{ color: '#6b3410' }}>Listino LONGRUN</Label>
                    </div>
                    <Switch
                      checked={useLongrun}
                      onCheckedChange={(v) => { setUseLongrun(v); if (v) setUse4Vantage(false); }}
                      data-testid="longrun-switch"
                    />
                  </div>
                  <p className="text-xs mt-2" style={{ color: '#6b3410' }}>
                    Canone €{vehicle?.canone_longrun?.toLocaleString('it-IT')}/mese
                  </p>
                  <p className="text-xs mt-1 font-medium" style={{ color: '#6b3410' }}>
                    Condizioni fisse: 48 mesi • Km ILLIMITATI
                  </p>
                </div>
              )}

              {/* Commission selector (only for agents/admin) */}
              {canEditCommission && (
                <div className="space-y-3">
                  {/* Box Provvigioni - Discreto con blur di default */}
                  <div className="bg-slate-100 border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Margine
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowProvvigioni(!showProvvigioni)}
                        className="p-1 rounded hover:bg-slate-200 transition-colors"
                        title={showProvvigioni ? "Nascondi importo" : "Mostra importo"}
                      >
                        {showProvvigioni ? (
                          <EyeOff className="w-4 h-4 text-slate-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    <p 
                      className={cn(
                        "text-lg font-semibold text-slate-600 text-center transition-all duration-200",
                        !showProvvigioni && "blur-md select-none"
                      )}
                      data-testid="agent-commission"
                    >
                      €{Math.round(provvigioneAgente).toLocaleString('it-IT')}
                    </p>
                  </div>
                  
                  {/* Slider provvigioni */}
                  <Slider
                    value={[provScelta]}
                    onValueChange={(v) => setProvScelta(v[0])}
                    min={0}
                    max={provMaxAggiuntivo}
                    step={1}
                    className="py-2"
                    data-testid="commission-slider"
                  />
                </div>
              )}

              {/* ========== FRANCHIGIE SELECTOR ========== */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Franchigie Assicurative</Label>
                  {use4Vantage && has4Vantage && (
                    <Badge className="bg-violet-100 text-violet-700 text-xs">Fisso 4VANTAGE</Badge>
                  )}
                </div>
                
                {use4Vantage && has4Vantage ? (
                  // 4Vantage: mostra franchigie fisse (non modificabili)
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <div className="flex items-center gap-2 text-violet-700">
                      <span className="text-sm font-medium">€500 Danni • 0% Furto</span>
                    </div>
                    <p className="text-xs text-violet-500 mt-1">Incluso nel listino 4VANTAGE</p>
                  </div>
                ) : (
                  // Normale: selettore franchigie - SENZA indicazione costo extra
                  <Select value={franchigiaScelta} onValueChange={setFranchigiaScelta}>
                    <SelectTrigger data-testid="franchigia-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FRANCHIGIE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="font-medium">{opt.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* ========== AUTO SOSTITUTIVA SELECTOR ========== */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="auto-sost-toggle" className="text-sm font-medium cursor-pointer">
                    Auto Sostitutiva
                  </Label>
                  <Switch
                    id="auto-sost-toggle"
                    checked={autoSostitutivaEnabled}
                    onCheckedChange={setAutoSostitutivaEnabled}
                    data-testid="auto-sostitutiva-toggle"
                  />
                </div>
                {autoSostitutivaEnabled && (
                  <div className="space-y-2">
                    <Select value={autoSostitutivaCategoria} onValueChange={setAutoSostitutivaCategoria}>
                      <SelectTrigger data-testid="auto-sostitutiva-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['B>8', 'C>8', 'D>8'].map(cat => {
                          const tipo = use4Vantage ? '4vantage' : (isUsato ? 'usato' : 'nuovo');
                          const costo = dbParams?.auto_sostitutiva_costi?.[cat]?.[tipo] || 0;
                          return (
                            <SelectItem key={cat} value={cat}>
                              <span className="font-medium">Categoria {cat}</span>
                              {costo > 0 && <span className="text-xs text-slate-500 ml-2">(+€{costo.toFixed(2)}/mese)</span>}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500 leading-tight">
                      Auto sostitutiva oltre le 8 ore lavorative, km illimitati. Il simbolo &gt;8 indica la disponibilità oltre l'orario standard.
                    </p>
                  </div>
                )}
              </div>

              {/* ========== PROMO NUOVO CLIENTE / RINNOVO ========== */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <Label htmlFor="promo-toggle" className={`text-sm font-medium ${use4Vantage ? 'text-slate-400' : 'cursor-pointer'}`}>
                      Promo Nuovo Cliente / Rinnovo
                    </Label>
                    {use4Vantage && (
                      <span className="text-[11px] text-slate-400 italic">Non disponibile con listino 4Vantage</span>
                    )}
                  </div>
                  <Switch
                    id="promo-toggle"
                    checked={promoEnabled}
                    onCheckedChange={setPromoEnabled}
                    disabled={use4Vantage}
                    data-testid="promo-toggle"
                  />
                </div>
                {promoAttiva && promoImporto > 0 && (
                  <div
                    className="p-3 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50"
                    data-testid="promo-badge"
                  >
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-500 text-white hover:bg-amber-600 text-xs font-semibold">PROMO</Badge>
                      <span className="text-sm font-semibold text-amber-900">
                        Sconto Promo applicato: €{promoImporto.toLocaleString('it-IT')}
                      </span>
                    </div>
                    <p className="text-[11px] text-amber-700 mt-1">
                      Suddivisi sulla durata: −€{scontoPromo.toFixed(2)}/mese detratti dal canone. Non sommati all'anticipo.
                    </p>
                  </div>
                )}
              </div>

              {/* ========== GOMME SELECTOR ========== */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Pneumatici Aggiuntivi</Label>
                  {use4Vantage && has4Vantage && (
                    <Badge className="bg-violet-100 text-violet-700 text-xs">4 Gomme Incluse</Badge>
                  )}
                </div>
                
                {use4Vantage && has4Vantage ? (
                  // 4Vantage: mostra gomme incluse (non modificabili)
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <div className="flex items-center gap-2 text-violet-700">
                      <span className="text-sm font-medium">4 Gomme in Sostituzione</span>
                    </div>
                    <p className="text-xs text-violet-500 mt-1">Treno completo incluso nel canone</p>
                  </div>
                ) : (
                  // Normale: selettore gomme
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Gomme Invernali</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="8"
                          value={gommeInvernali}
                          onChange={(e) => setGommeInvernali(Math.min(8, Math.max(0, parseInt(e.target.value) || 0)))}
                          className="w-16 text-center"
                          data-testid="gomme-invernali-input"
                        />
                        <span className="text-xs text-slate-500">
                          {effectiveDuration && (
                            <>€{getPrezzoGommaUnitario('invernali').toFixed(2)}/pz</>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Gomme Estive</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="8"
                          value={gommeEstive}
                          onChange={(e) => setGommeEstive(Math.min(8, Math.max(0, parseInt(e.target.value) || 0)))}
                          className="w-16 text-center"
                          data-testid="gomme-estive-input"
                        />
                        <span className="text-xs text-slate-500">
                          {effectiveDuration && (
                            <>€{getPrezzoGommaUnitario('estive').toFixed(2)}/pz</>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Mostra costo gomme se > 0 */}
                {!use4Vantage && costoGomme > 0 && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
                    <span className="font-medium">Costo pneumatici:</span> +€{costoGomme.toFixed(2)}/mese
                    <span className="text-blue-400 ml-1">
                      ({gommeInvernali > 0 && `${gommeInvernali} inv.`}{gommeInvernali > 0 && gommeEstive > 0 && ' + '}{gommeEstive > 0 && `${gommeEstive} est.`})
                    </span>
                  </div>
                )}
              </div>

              {/* Anticipo */}
              <div className="space-y-2">
                <Label>Anticipo (€) <span className="text-xs text-red-600 font-normal">IVA esclusa</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={anticipo}
                  onChange={(e) => setAnticipo(parseFloat(e.target.value) || 0)}
                  data-testid="anticipo-input"
                />
                <p className="text-xs text-slate-500">L&apos;anticipo verrà diviso per la durata del noleggio e sottratto dal canone mensile</p>
              </div>

              {/* Final price */}
              {canoneBase > 0 && (
                <div className={`p-4 rounded-lg text-white ${use4Vantage ? 'bg-violet-700' : isUsato ? 'bg-emerald-700' : 'bg-slate-800'}`}>
                  <p className="text-sm opacity-80">Canone finale mensile</p>
                  <p className="text-3xl font-bold mt-1" data-testid="final-price">
                    €{Math.round(finalPrice).toLocaleString('it-IT')}
                  </p>
                  <p className="text-xs opacity-70 mt-1">
                    {use4Vantage ? `4VANTAGE • ${VANTAGE_DURATION} mesi • ${VANTAGE_KM.toLocaleString('it-IT')} km` : 'Standard'}
                    {scontoAnticipo > 0 && ` • -€${Math.round(scontoAnticipo).toLocaleString('it-IT')}/mese anticipo`}
                  </p>
                  <p className="text-xs text-red-300 mt-2 font-medium">* IVA esclusa</p>
                </div>
              )}

              {/* Create offer button */}
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className={`w-full btn-scale ${isUsato ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[hsl(25,95%,53%)] hover:bg-[hsl(25,95%,48%)]'}`}
                    disabled={!canoneBase}
                    data-testid="create-offer-btn"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Genera Offerta PDF
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
                      {isClientOrProspect ? (
                        <>
                          <FileDown className="w-5 h-5 inline mr-2" />
                          Genera il tuo Preventivo
                        </>
                      ) : (
                        <>
                          <BookUser className="w-5 h-5 inline mr-2" />
                          Associa Cliente all&apos;Offerta
                        </>
                      )}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    
                    {/* Vista Cliente: Riepilogo parametri auto (no selezione anagrafica) */}
                    {isClientOrProspect ? (
                      <div className="space-y-4">
                        {/* Riepilogo Veicolo */}
                        <div className="p-4 bg-slate-50 rounded-lg border">
                          <h4 className="font-medium text-slate-800 mb-2">Riepilogo Veicolo</h4>
                          <p className="font-semibold text-lg">{vehicle?.marca} {vehicle?.modello}</p>
                          <p className="text-sm text-slate-500">{vehicle?.versione}</p>
                        </div>
                        
                        {/* Parametri Selezionati */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="font-medium text-blue-800 mb-3">Parametri Selezionati</h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-blue-600">Durata:</span>
                              <p className="font-semibold">{effectiveDuration} mesi</p>
                            </div>
                            <div>
                              <span className="text-blue-600">Km/anno:</span>
                              <p className="font-semibold">{effectiveKm?.toLocaleString('it-IT')} km</p>
                            </div>
                            <div>
                              <span className="text-blue-600">Anticipo:</span>
                              <p className="font-semibold">€{anticipo.toLocaleString('it-IT')}</p>
                            </div>
                            <div>
                              <span className="text-blue-600">Canone Mensile:</span>
                              <p className="font-semibold text-lg text-primary">€{Math.round(finalPrice).toLocaleString('it-IT')}/mese</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Note opzionali */}
                        <div className="space-y-2">
                          <Label>Note (opzionale)</Label>
                          <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Inserisci eventuali note o richieste..."
                            className="resize-none"
                            rows={2}
                          />
                        </div>
                        
                        <p className="text-xs text-slate-500 text-center">
                          Il preventivo verrà associato automaticamente al tuo profilo
                        </p>
                      </div>
                    ) : (
                      /* Vista Admin/Agente: Selezione contatto completa */
                      <>
                        {/* Contact selection toggle */}
                        <div className="flex gap-2 mb-4">
                          <Button
                            variant={!showNewContactForm ? "default" : "outline"}
                            className="flex-1"
                            onClick={() => setShowNewContactForm(false)}
                          >
                            Seleziona dalla Rubrica
                          </Button>
                          <Button
                            variant={showNewContactForm ? "default" : "outline"}
                            className="flex-1"
                            onClick={() => setShowNewContactForm(true)}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Nuovo Contatto
                          </Button>
                        </div>

                        {!showNewContactForm ? (
                          /* Select existing contact */
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>Seleziona Contatto *</Label>
                              <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                                <SelectTrigger data-testid="contact-select">
                                  <SelectValue placeholder="Cerca nella rubrica..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {[...contacts]
                                    .sort((a, b) => (a.nome_cognome || '').localeCompare(b.nome_cognome || '', 'it', { sensitivity: 'base' }))
                                    .map(c => {
                                      const initial = (c.nome_cognome || '?').trim().charAt(0).toUpperCase() || '?';
                                      return (
                                        <SelectItem key={c.id} value={c.id}>
                                          <div className="flex items-center gap-2">
                                            <Badge className="bg-slate-100 text-slate-700 font-bold w-6 h-6 flex items-center justify-center p-0 rounded-full">
                                              {initial}
                                            </Badge>
                                            {c.nome_cognome} - {c.telefono}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                </SelectContent>
                              </Select>
                            </div>
                            {selectedContactId && (
                              <div className="p-3 bg-slate-50 rounded-lg">
                                {(() => {
                                  const contact = contacts.find(c => c.id === selectedContactId);
                                  return contact ? (
                                    <div className="text-sm">
                                      <p className="font-medium">{contact.nome_cognome}</p>
                                      <p className="text-slate-500">{contact.email} • {contact.telefono}</p>
                                      {contact.citta && <p className="text-slate-500">{contact.citta}</p>}
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            )}
                          </div>
                        ) : (
                      /* New contact form */
                      <div className="space-y-4">
                        {/* Profile type toggle */}
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm font-medium">Tipo Profilo</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${newContactTipo === 'smart' ? 'font-semibold text-blue-600' : 'text-slate-400'}`}>Smart</span>
                            <Switch
                              checked={newContactTipo === 'prospect'}
                              onCheckedChange={(checked) => setNewContactTipo(checked ? 'prospect' : 'smart')}
                            />
                            <span className={`text-xs ${newContactTipo === 'prospect' ? 'font-semibold text-violet-600' : 'text-slate-400'}`}>Prospect</span>
                          </div>
                        </div>

                        {/* Common fields */}
                        <div className="space-y-2">
                          <Label>Nome e Cognome / Ragione Sociale *</Label>
                          <Input
                            value={newContactData.nome_cognome}
                            onChange={(e) => setNewContactData({ ...newContactData, nome_cognome: e.target.value })}
                            placeholder="Es. Mario Rossi"
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Telefono *</Label>
                            <Input
                              value={newContactData.telefono}
                              onChange={(e) => setNewContactData({ ...newContactData, telefono: e.target.value })}
                              placeholder="333 1234567"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                              type="email"
                              value={newContactData.email}
                              onChange={(e) => setNewContactData({ ...newContactData, email: e.target.value })}
                              placeholder="email@esempio.it"
                              required
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Città {newContactTipo === 'prospect' ? '*' : ''}</Label>
                            <Input
                              value={newContactData.citta}
                              onChange={(e) => setNewContactData({ ...newContactData, citta: e.target.value })}
                              placeholder="Roma"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Responsabile {newContactTipo === 'prospect' ? '*' : ''}</Label>
                            <Input
                              value={newContactData.responsabile}
                              onChange={(e) => setNewContactData({ ...newContactData, responsabile: e.target.value })}
                              placeholder="Nome referente"
                            />
                          </div>
                        </div>

                        {/* Prospect fields */}
                        {newContactTipo === 'prospect' && (
                          <>
                            <div className="pt-2 border-t">
                              <p className="text-xs text-violet-600 font-medium mb-3">Campi Prospect</p>
                              <div className="space-y-3">
                                <div className="space-y-2">
                                  <Label>Tipologia Cliente *</Label>
                                  <Select value={newContactData.tipologia_cliente} onValueChange={(v) => setNewContactData({ ...newContactData, tipologia_cliente: v })}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Seleziona..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {TIPOLOGIE_CLIENTE.map(t => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-2">
                                    <Label>Codice Fiscale</Label>
                                    <Input
                                      value={newContactData.codice_fiscale}
                                      onChange={(e) => setNewContactData({ ...newContactData, codice_fiscale: e.target.value.toUpperCase() })}
                                      placeholder="RSSMRA80A01H501Z"
                                      maxLength={16}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Partita IVA</Label>
                                    <Input
                                      value={newContactData.partita_iva}
                                      onChange={(e) => setNewContactData({ ...newContactData, partita_iva: e.target.value })}
                                      placeholder="12345678901"
                                      maxLength={11}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label>Indirizzo *</Label>
                                  <Input
                                    value={newContactData.indirizzo}
                                    onChange={(e) => setNewContactData({ ...newContactData, indirizzo: e.target.value })}
                                    placeholder="Via Roma 123"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-2">
                                    <Label>CAP *</Label>
                                    <Input
                                      value={newContactData.cap}
                                      onChange={(e) => setNewContactData({ ...newContactData, cap: e.target.value })}
                                      placeholder="00100"
                                      maxLength={5}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Km annui previsti *</Label>
                                    <Input
                                      type="number"
                                      value={newContactData.km_annui_previsti}
                                      onChange={(e) => setNewContactData({ ...newContactData, km_annui_previsti: e.target.value })}
                                      placeholder="15000"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                      </>
                    )}

                    {showNewContactForm && (
                      <AyvensQuestion value={newContactAyvens} onChange={setNewContactAyvens} variant="staff" testIdPrefix="new-contact-ayvens" />
                    )}

                    {/* Notes - solo per Admin/Agente (clienti hanno già il campo sopra) */}
                    {!isClientOrProspect && (
                      <div className="space-y-2 pt-2 border-t">
                        <Label>Note Offerta</Label>
                        <Textarea
                          placeholder="Note aggiuntive..."
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          data-testid="offer-notes-input"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Annulla
                    </Button>
                    <Button 
                      className={`flex-1 ${isUsato ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[hsl(25,95%,53%)] hover:bg-[hsl(25,95%,48%)]'}`}
                      onClick={handleCreateOffer}
                      disabled={isCreating || (!isClientOrProspect && !showNewContactForm && !selectedContactId)}
                      data-testid="confirm-offer-btn"
                    >
                      {isCreating ? 'Creazione...' : 'Crea e Scarica PDF'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </div>
      <CanoneMismatchDialog
        data={canoneMismatch}
        onCancel={() => setCanoneMismatch(null)}
        onConfirm={confirmCanoneMismatch}
      />
    </div>
  );
};

export default VehicleDetailPage;
